import _ from 'underscore';
import { endOfDay, isAfter, parseISO, startOfDay, subDays } from 'date-fns';
import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { broadcastTypeTitlesHash } from '@culture/utils/helpers/broadcast';
import { filterQuery } from '@culture/common-helpers/url';

import CommonPropTypes from 'utils/propTypes/common';
import {
	catalogsActions,
	catalogsOperations,
	catalogsSelectors
} from 'ducks/catalogs';
import dateHelpers from 'utils/helpers/dates';
import { getFilterDateParams } from 'utils/helpers/filters';

import Catalog from '../components/Catalog';

const CatalogContainer = ({ total, limit, urlParams, broadcasts, rubric }) => {
	const dispatch = useDispatch();
	const filters = useSelector(catalogsSelectors.selectFilterValues);
	const { push, asPath, pathname } = useRouter();

	useEffect(() => {
		let modifiedParams = {
			..._(urlParams).omit('startDate', 'endDate'),
			type: 'broadcast'
		};

		if (!urlParams.startDate) {
			modifiedParams = {
				...modifiedParams,
				publishDateFrom: startOfDay(subDays(new Date(), 1)).toISOString()
			};
		} else {
			modifiedParams = filterQuery({
				...modifiedParams,
				publishDateFrom:
					urlParams.startDate &&
					startOfDay(parseISO(urlParams.startDate)).toISOString(),
				publishDateTo:
					urlParams.endDate &&
					endOfDay(parseISO(urlParams.endDate)).toISOString()
			});
		}

		if (urlParams.type) {
			modifiedParams = {
				...modifiedParams,
				broadcastType: urlParams.type
			};
		}

		dispatch(
			catalogsOperations.getFilterValues(
				'catalogFilters/live/schedule',
				modifiedParams
			)
		);
	}, [asPath]);

	useEffect(() => () => dispatch(catalogsActions.resetFilterValues()), []);

	const handleFilterChange = useCallback(
		(name, newValue) => {
			let currentValue = urlParams[name] || '';

			let pushParams;
			let value = newValue;

			if (
				name === 'dates' ||
				Object.keys(dateHelpers.dateHash).includes(name)
			) {
				currentValue = _(urlParams).pick('startDate', 'endDate');

				value =
					name === 'dates'
						? getFilterDateParams(value.startDate, value.endDate)
						: dateHelpers.dateHash[name].values();

				pushParams = {
					..._(urlParams).omit('dates'),
					...value
				};
			} else {
				pushParams = {
					...urlParams,
					[`${name}`]: value
				};
			}

			if (!_.isEqual(value, currentValue)) {
				push({
					pathname,
					query: filterQuery({
						...pushParams,
						page: null
					})
				});
			}
		},
		[urlParams]
	);

	const handleFilterReset = useCallback(() => {
		push({ pathname, query: {} });
	}, []);

	const filterItems = useMemo(() => {
		const dateParams = _(urlParams).pick('startDate', 'endDate');

		const prepareTypes = (types) =>
			_(types).map((type) => ({
				name: type,
				title: broadcastTypeTitlesHash[type]
			}));

		const datesHash = _(dateHelpers.dateHash).map((value, key) => ({
			title: value.title,
			isShow: _(filters.dates).some((date) => {
				const parsedDate = parseISO(date);

				return (
					(isAfter(parsedDate, parseISO(value.values().startDate)) ||
						value.values().startDate === date) &&
					(isAfter(parseISO(value.values().endDate), parsedDate) ||
						value.values().endDate === date)
				);
			}),
			key,
			type: 'button',
			value: key,
			active: _.isEqual(value.values(), dateParams)
		}));

		return [
			{
				values: filters.dates || [],
				dates: dateParams,
				key: 'dates',
				name: 'dates',
				type: 'date'
			},
			...datesHash,
			{
				title: 'Все\u00A0тематики',
				values: (filters.types && prepareTypes(filters.types)) || [],
				key: 'type',
				icon: 'dots',
				type: 'select'
			},
			{
				title: 'Нацпроект',
				isShow: filters.isNationalProjectParticipant,
				key: 'isNationalProjectParticipant',
				icon: urlParams.isNationalProjectParticipant
					? 'catalogToggleActive'
					: 'catalogToggle',
				type: 'button',
				value: !urlParams.isNationalProjectParticipant || null,
				active: !!urlParams.isNationalProjectParticipant
			}
		];
	}, [filters, urlParams]);

	return (
		<Catalog
			onFilterChange={handleFilterChange}
			onFilterReset={handleFilterReset}
			filters={filterItems}
			filtersLoaded={!_.isEmpty(filters)}
			total={total}
			limit={limit}
			urlParams={urlParams}
			broadcasts={broadcasts}
			title={rubric.headingTitle}
		/>
	);
};

CatalogContainer.propTypes = {
	rubric: CommonPropTypes.rubric,
	total: PropTypes.number.isRequired,
	broadcasts: PropTypes.array.isRequired,
	limit: PropTypes.number.isRequired,
	urlParams: PropTypes.object.isRequired
};

CatalogContainer.defaultProps = {
	rubric: {}
};

export default CatalogContainer;
